/* eslint no-param-reassign: 0 */
import { ImportUsersConsts } from 'client/util/Constants';
import { cloneDeep, extend, uniqueId } from 'lodash';
import JobApi from 'client/api/v2/JobApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import Vue from 'vue';

const { importResultStatus, searchProperties, source } = ImportUsersConsts;

// --------------------------------------------------------------------------
// Private Helper Methods
// --------------------------------------------------------------------------

/**
 * Function to extract and return a
 * display-friendly creation source string
 * Examples:
 * - For jumpcloud:scim:slack, return slack
 * - For jumpcloud:gsuite, return G Suite
 * @param {String} creationSource string to format Ex: jumpcloud:scim:slack
 * @returns Display-friendly creation source string
 */
const formatCreationSource = (creationSource) => {
  let formattedSource = 'Unknown source';
  switch (creationSource) {
    // Cases are for sources with the format:
    // jumpcloud:<Creation source>
    case source.default:
      formattedSource = 'Bulk';
      break;

    case source.gapps:
      formattedSource = 'Google';
      break;

    case source.o365:
      formattedSource = 'M365';
      break;

    case source.workday:
      formattedSource = 'Workday';
      break;

    default:
      // Here we manage special format sources
      // Ex: jumpcloud:scim:bookmark:<appName>
      try {
        // If in the future many special format sources appear,
        // create an aux function to parse/handle them
        const splitRegex = /(jumpcloud:scim|jumpcloud:custom_integration):/;
        if (splitRegex.test(creationSource)) {
          formattedSource = creationSource.split(splitRegex).pop();
        }
        break;
      } catch (err) {
        break;
      }
  }
  return formattedSource;
};

/**
 * @TODO
 * In order to avoid calling another search request to the API.
 * The addition of the creation source has been embedded into the
 * addIsNewFieldToUsers helper function.
 * A good idea would be to have a separation of concerns here.
 * Maybe have a parent function that receives a list of different
 * properties, not just only email and creationSource.
 * This parent function makes the request and then call aux
 * functions to set all necessary properties
 */

/**
 * Helper for fetchCollection. Add isNew: true to all the users not already
 * imported by comparing gsuite email to jc email
 *
 * @param {Array} users - users to add isNew field to
 */
export const addIsNewFieldToUsers = async (users, emailField) => {
  // Search jumpcloud for all user emails
  const emails = users.map(user => ({ email: user[emailField] }));
  const filter = { or: emails };
  const items = await SystemUserApi.search(filter, 'email creationSource');

  const existingUsers = items.results
    .map(result => ({ email: result.email, creationSource: result.creationSource }));

  return users.map((user) => {
    const existingUser = existingUsers.find(
      (existing) => (user[emailField]) && (user[emailField].toLowerCase() === existing.email),
    );

    if (!existingUser) {
      user.isNew = true;
    } else if (existingUser.creationSource) {
      user.creationSource = formatCreationSource(existingUser.creationSource);
    }

    return user;
  });
};

/**
 * Filter users that meet the filter criteria
 * @param {String} sortField Key to filter by
 * @param {String} fieldValue Value of key to filter by
 * @param {Array} users Array of users
 * @returns Array
 */
export const filterItemsByField = (users = [], sortField, fieldValue) => {
  let returnUsersArray = users;
  if (sortField === '' && fieldValue === '') {
    returnUsersArray = users;
  } else returnUsersArray = users.filter((user) => user[sortField] === fieldValue);

  return returnUsersArray;
};

/**
 * @description Sort the results from the import.
 *
 * Note: I put the `if (descending)` check outside of the sort even though it's
 * repetitive, to avoid calling it up to `results.length ^ 2` times when the
 * list is sorted  - per code review suggestion.
 */
export const sortItemsByField = (sortField, results = []) => {
  if (!sortField) {
    return;
  }

  const descending = !!String(sortField).match(/^-/);
  const field = sortField.replace(/^-/, '');

  if (descending) {
    results.sort((a, b) => {
      const aField = String(a[field]).toLowerCase();
      const bField = String(b[field]).toLowerCase();

      if (aField > bField) {
        return -1;
      }
      if (aField < bField) {
        return 1;
      }
      return 0;
    });
  } else {
    // Ascending order by field sort
    results.sort((a, b) => {
      const aField = String(a[field]).toLowerCase();
      const bField = String(b[field]).toLowerCase();

      if (aField < bField) {
        return -1;
      }
      if (aField > bField) {
        return 1;
      }
      return 0;
    });
  }
};

/**
 * Iterate over the searchProperties array to find the type of user received
 *
 * We just need one item from the array to evaluate the type
 * @param {Object} item One user item from the items array
 * @returns String user type identification property
 * Example: for a Google user returns 'primaryEmail'
 */

export const searchProperty = item => searchProperties.find(property => (item[property]));

export const updateState = (state) => {
  const state2 = cloneDeep(state);

  state2.entities.allUsers = { byId: {} };
  state2.entities.importedUsers = { byId: {} };
  state2.entities.validationErrors = [];
  state2.features.bulkImportLimit = 500;
  state2.features.errorPollingImportResults = false;
  state2.features.importError = null;
  state2.features.isBulkImport = false;
  state2.features.isImportComplete = false;
  state2.features.jobId = '';

  return state2;
};

/**
 * The ImportedUsersMixin can be used by Vuex modules that allow the bulk
 * import of users into JumpCloud. Currently this mixin is used by the
 * ImportedUsersCollection.
 *
 * Eventually the WorkdayWorkerCollection can be refactored to use this mixin as
 * well. And it is assumed that someday, new Vuex modules that allow the import
 * of users from G Suite, Microsoft 365 and Active Directory will use it as well.
 * of users from Google, Microsoft 365 and Active Directory will use it as well.
 * Or perhaps the ImportUsersCollection can be used more generically to import
 * users from any of these sources..?
 *
 * Note that this mixin is intended to be used in a module that extends the
 * CollectionModule class because it expects actions, getters, and mutations
 * defined there to be present.
 */
export default {
  actions: {

    /**
     * @description Adds error to validationErrors array if a duplicate doesn't exist.
     */
    addValidationError({ commit, state }, { selectedItemId, fieldName }) {
      const errorObject = { selectedItemId, fieldName };
      const errorIndex = state.entities.validationErrors
        .findIndex(i => i.selectedItemId === selectedItemId && i.fieldName === fieldName);
      if (errorIndex === -1) {
        commit('ADD_VALIDATION_ERROR', errorObject);
      }
    },

    /**
     * @description Fetch the next set of users from the allUsers object in the entities,
     * set the collection so the next set of items is displayed in the UI, and
     * return the IDs of the next set of items to the caller.
     */
    fetchCollection({ commit, state }, { next = false } = {}) {
      return new Promise((resolve) => {
        let { skip } = state.features.items.pagination;
        const { sort } = state.features.items;
        const { allUsers } = state.entities;
        const users = Object.keys(allUsers.byId).map(user => allUsers.byId[user]);
        /**
         * @todo setting the limit to the length of the users array. This is due
         * to the client side vee validate only validating rendered items. For workday
         * this could be modified to a ternary to restore the paginiation by passing and
         * argument to this method.
         */
        const limit = users.length;

        if (next === true) {
          skip += limit;

          if (skip >= state.entities.items.totalCount) {
            resolve();
            return;
          }

          commit('SET_PAGINATION_SKIP', { skip });
        }
        sortItemsByField(sort, users);

        const results = users.slice(skip, skip + limit);

        const payload = {
          results,
          totalCount: users.length,
          totalCountOfAllItems: users.length,
        };

        commit('SET_COLLECTION', payload);

        resolve(results.map(result => result.id));
      });
    },

    /**
     * @description Call JobApi.getImportResults() to get the import results for the specified
     * job, update the status of the imported users in the state, and then return
     * the number of unfinished users to the caller so that the caller can continue
     * calling this method until all users have been imported into JumpCloud.
     */
    getImportResults(context) {
      const { features } = context.state;
      const { jobId } = features;

      return JobApi
        .getImportResults(jobId)
        .then((results) => {
          // Update importedUsers list with results from JobApi
          context.commit('UPDATE_IMPORTED_USERS', { results });

          // Return the number of unfinished users, i.e. those with a status of
          // pending, started or in progress, to the caller so that the caller
          // can continue calling getImportResults() until all users have been
          // imported into JumpCloud.
          const { pending, started, inProgress } = importResultStatus;

          const unfinishedUsers = results.reduce((accumulator, result) => {
            switch (result.status) {
              case pending:
              case started:
              case inProgress:
                accumulator.push(result);
                break;
              default:
                break;
            }
            return accumulator;
          }, []);

          return unfinishedUsers.length;
        });
    },

    /**
     * @description To import the selected users, first populate them in the state so that
     * they can be displayed in the import component. Then create an array of system users
     * and pass them to the `importUsers` method. The `importUsers` method will return a job ID
     * and if the number of system users is less than bulkImportLimit, it will begin polling to
     * get the import results.
     * @param processUsers Function provided by the caller to perform additional mapping of users
     * prior to passing them to the JobApi.importUsers() method. This can be used to translate
     * field names and massage values.
     * @param sourceCreation Defines the creation-source header for gapps, o365 and workdays
     * requests. If the header isn't sent, the default value is jumpcloud:bulk, if you send
     * the header with a malformed value you receive a 400 error.
     */
    importSelectedUsers(context, processUsers = users => users, sourceCreation = source.default) {
      const { commit, dispatch, state } = context;
      const { entities, features } = state;

      const users = Object.values(entities.boundItems.byId);
      const isBulk = users.length > features.bulkImportLimit;

      commit('SET_IS_BULK_IMPORT', isBulk);

      users.forEach((user) => {
        if (user.password === '') {
          delete user.password;
        }
      });

      const processedUsers = processUsers(users);

      return JobApi
        .importUsers(processedUsers, sourceCreation)
        .then((jobId) => {
          commit('SET_JOB_ID', jobId);

          if (!isBulk) {
            commit('RESET_IMPORTED_USERS');
            dispatch('pollGetImportResults');
          }
        });
    },

    importUpdateUsers({ commit }, { id, queryString, operations }) {
      return JobApi
        .createImportUsersJob(id, queryString, operations)
        .then((jobId) => {
          commit('SET_JOB_ID', jobId);
        });
    },

    /**
     * @description Poll the getImportResults() method in a timeout until all imported users
     * have been imported.
     */
    pollGetImportResults({ commit, dispatch }) {
      return dispatch('getImportResults')
        .then((numUnfinishedUsers) => {
          if (numUnfinishedUsers > 0) {
            window.setTimeout(() => dispatch('pollGetImportResults'), 250);
          } else {
            commit('SET_IS_IMPORT_COMPLETE', true);
          }
        })
        .catch(() => {
          commit('SET_ERROR_POLLING_IMPORT_RESULTS');
        });
    },

    /**
     * @description Ensure each item has a unique ID if not already present and then commit
     * the SET_ALL_USERS mutation to populate the state.
     */
    populateImportUsers({ commit }, users = []) {
      const users2 = users.map(user => ({ id: uniqueId(), ...user }));
      commit('SET_ALL_USERS', users2);
    },

    /**
     * @description Removes error from validationErrors array if the error passed in is found.
     */
    removeValidationError({ commit, state }, { selectedItemId, fieldName }) {
      const errorIndex = state.entities.validationErrors
        .findIndex(i => i.selectedItemId === selectedItemId && i.fieldName === fieldName);
      if (errorIndex > -1) {
        commit('REMOVE_VALIDATION_ERROR', errorIndex);
      }
    },

    /**
     * @description Allow the component to set the isImportComplete state
     */
    setIsImportComplete({ commit }, isComplete = false) {
      commit('SET_IS_IMPORT_COMPLETE', isComplete);
    },

    updateItem({ commit }, { id, key, value }) {
      commit('UPDATE_ITEM', { id, key, value });
    },

    unsetImportError({ commit }) {
      commit('SET_IMPORT_ERROR', null);
    },
  },

  getters: {
    getImportError(state) {
      return state.features.importError;
    },

    getImportedUsers(state) {
      return Object.values(state.entities.importedUsers.byId);
    },

    getIsBulkImport(state) {
      return state.features.isBulkImport;
    },

    getIsImportComplete(state) {
      return state.features.isImportComplete;
    },

    getJobId(state) {
      return state.features.jobId;
    },

    getValidationErrors(state) {
      return state.entities.validationErrors;
    },

    getValidationErrorCount(state) {
      return state.entities.validationErrors.length;
    },
  },

  mutations: {
    ADD_VALIDATION_ERROR(state, error) {
      state.entities.validationErrors.push(error);
    },

    REMOVE_VALIDATION_ERROR(state, index) {
      state.entities.validationErrors.splice(index, 1);
    },

    RESET_IMPORTED_USERS(state) {
      Vue.set(state.entities.importedUsers, 'byId', {});
    },

    SELECT_ALL_ITEMS(state, { ids }) {
      const newIds = ids;

      state.features.selectedItems.ids = newIds;
      state.features.unselectedItems.ids = [];
    },

    SET_ALL_USERS(state, users) {
      const { allUsers } = state.entities;
      Vue.set(allUsers, 'byId', {});
      users.forEach(user => Vue.set(allUsers.byId, user.id, user));
    },

    SET_ERROR_POLLING_IMPORT_RESULTS(state) {
      state.features.errorPollingImportResults = true;
    },

    SET_IMPORT_ERROR(state, error) {
      state.features.importError = error;
    },

    SET_IS_BULK_IMPORT(state = { features: {} }, isBulkImport = false) {
      state.features.isBulkImport = isBulkImport;
    },

    SET_IS_IMPORT_COMPLETE(state, isImportComplete = false) {
      state.features.isImportComplete = isImportComplete;
    },

    SET_JOB_ID(state, jobId) {
      state.features.jobId = jobId;
    },

    UNSELECT_ALL_ITEMS(state, { ids }) {
      const newIds = [...ids];

      state.features.unselectedItems.ids = newIds;
      state.features.selectedItems.ids = [];
    },

    /**
     * @description For each result, update the status and status message in
     * the importedUsers list
     */
    UPDATE_IMPORTED_USERS(state, { results }) {
      results.forEach((result) => {
        const { id } = result;
        const { systemUser } = result.meta;

        const {
          email,
          firstname,
          lastname,
          username,
        } = systemUser;

        Vue.set(state.entities.importedUsers.byId, id, {
          email,
          firstname,
          id,
          importMessage: result.statusMsg || result.status,
          importStatus: result.status,
          lastname,
          username,
        });
      });
    },

    /**
     * @description When updating an item, a deep copy is first made, and then the key is
     * updated with the new value in both the original and deep copy. This is
     * done so that the modifications aren't lost when searching and sorting.
     */
    UPDATE_ITEM(state = {
      entities: {
        boundItems: {
          byId: {},
        },
        items: {
          byId: {},
        },
      },
    }, { id, key, value }) {
      if (!state.entities.boundItems.byId[id]) {
        state.entities.boundItems.byId[id] = extend(
          {},
          state.entities.items.byId[id],
        );
      }

      state.entities.items.byId[id][key] = value;
      state.entities.boundItems.byId[id][key] = value;
    },
  },
};
